import { Box } from '@mui/material';
import FormLabel from '@mui/material/FormLabel';
import { Theme } from '@mui/system/createTheme';
import useMediaQuery from '@mui/system/useMediaQuery';
import { DateRange } from '@mui/x-date-pickers-pro';
import {
  DateRangePicker as MuiDateRangePicker,
  DateRangePickerProps as MuiDateRangePickerProps,
} from '@mui/x-date-pickers-pro/DateRangePicker';
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import {
  StaticDateRangePicker as MuiStaticDateRangePicker,
  StaticDateRangePickerProps as MuiStaticDateRangePickerProps,
} from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import {
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';

type DateRangeShortcut = {
  label: string;
  getValue: () => DateRange<Date>;
};

type OmittedProps = Omit<
  MuiDateRangePickerProps<Date>,
  'open' | 'onOpen' | 'onClose'
>;

export interface DateRangePickerProps extends OmittedProps {
  label: React.ReactNode;
  floatingLabel?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
  error?: boolean;
  multiInput?: boolean;
  allTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
  showShortcuts?: boolean;
  shortcutItems?: DateRangeShortcut[];
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  floatingLabel,
  size,
  helperText,
  error,
  multiInput = false,
  allTime = false,
  minDate,
  maxDate,
  showShortcuts = true,
  shortcutItems,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const anchorElRef = useRef(null);
  const fieldProps = {
    label,
    floatingLabel,
    size,
    helperText,
    error,
    open: () => setOpen(true),
    ...(multiInput && { sx: { alignItems: 'center' } }),
  } as any;

  // Shared shortcuts configuration
  const today = useMemo(() => new Date(), []);
  const Input = multiInput
    ? MultiInputDateRangeField
    : SingleInputDateRangeField;

  const shortcuts = useMemo((): DateRangeShortcut[] => {
    if (shortcutItems) {
      return shortcutItems;
    }

    return [
      {
        label: 'Last 7 days',
        getValue: () => [subDays(today, 6), today],
      },
      {
        label: 'Last week',
        getValue: () => {
          const lastWeekStart = subDays(today, today.getDay() + 7);
          const lastWeekEnd = subDays(today, today.getDay() + 1);
          return [lastWeekStart, lastWeekEnd];
        },
      },
      {
        label: 'This week',
        getValue: () => {
          return [startOfWeek(today), endOfWeek(today)];
        },
      },
      {
        label: 'Last 30 days',
        getValue: () => [subDays(today, 29), today],
      },
      {
        label: 'Last month',
        getValue: () => {
          const lastMonth = subMonths(today, 1);
          return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
        },
      },
      {
        label: 'This month',
        getValue: () => {
          return [startOfMonth(today), endOfMonth(today)];
        },
      },
      {
        label: 'Last 90 days',
        getValue: () => [subDays(today, 89), today],
      },
      {
        label: 'Last quarter',
        getValue: () => {
          const lastQuarter = subQuarters(today, 1);
          return [startOfQuarter(lastQuarter), endOfQuarter(lastQuarter)];
        },
      },
      {
        label: 'This quarter',
        getValue: () => {
          return [startOfQuarter(today), endOfQuarter(today)];
        },
      },
      {
        label: 'Last year',
        getValue: () => [
          startOfYear(subYears(today, 1)),
          endOfYear(subYears(today, 1)),
        ],
      },
      {
        label: 'This year',
        getValue: () => [startOfYear(today), endOfYear(today)],
      },
    ];
  }, [shortcutItems, today]);

  if (allTime && !shortcuts.some((shortcut) => shortcut.label === 'All time')) {
    shortcuts.push({
      label: 'All time',
      getValue: () => [subYears(today, 10), addYears(today, 10)],
    });
  }

  return (
    <Box ref={anchorElRef}>
      <MuiDateRangePicker
        slots={{ field: Input }}
        slotProps={{
          field: fieldProps,
          shortcuts: {
            items: showShortcuts ? shortcuts : [],
          },
          actionBar: {
            actions: [],
          },
          popper: {
            anchorEl: anchorElRef.current,
          },
        }}
        calendars={2}
        {...props}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        minDate={minDate}
        maxDate={maxDate}
      />
    </Box>
  );
};

interface DateRangeFieldProps {
  floatingLabel?: boolean;
  label?: React.ReactNode;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  sx?: any;
  multiInput?: boolean;
  InputProps?: any;
  [key: string]: any; // for other props passed through
}

export const DateRangeField: React.FC<DateRangeFieldProps> = ({ ...props }) => {
  const {
    label,
    floatingLabel,
    sx,
    size,
    multiInput = false,

    // Dont spread these to FieldComponent
    helperText,
    hiddenLabel,
    open,
    fullWidth = true,

    ...otherProps
  } = props;

  const FieldComponent = multiInput
    ? MultiInputDateRangeField
    : SingleInputDateRangeField;

  return (
    <FormLabel
      sx={[
        { display: 'block' },
        fullWidth && { width: '100%' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {!floatingLabel && label && <Box sx={{ mb: 1 }}>{label}</Box>}
      <FieldComponent
        {...otherProps}
        label={floatingLabel && label}
        size={size}
        sx={{
          '& .MuiInputBase-input': {
            width: '100%',
            minWidth: 190,
          },
          ...otherProps.sx,
        }}
      />
    </FormLabel>
  );
};

interface StaticDateRangePickerProps
  extends MuiStaticDateRangePickerProps<Date> {
  dateRange: DateRange<Date>;
  onDateRangeChange: (newDateRange: DateRange<Date>) => void;
  allTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

export const StaticDateRangePicker: React.FC<StaticDateRangePickerProps> = ({
  dateRange,
  onDateRangeChange,
  allTime = false,
  minDate,
  maxDate,
  ...props
}) => {
  const today = useMemo(() => new Date(), []);
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const shortcuts = useMemo(
    () => [
      {
        label: 'Last 7 days',
        getValue: () => [subDays(today, 6), today],
      },
      {
        label: 'Last week',
        getValue: () => {
          const lastWeekStart = subDays(today, today.getDay() + 7);
          const lastWeekEnd = subDays(today, today.getDay() + 1);
          return [lastWeekStart, lastWeekEnd];
        },
      },
      {
        label: 'Last 30 days',
        getValue: () => [subDays(today, 29), today],
      },
      {
        label: 'Last month',
        getValue: () => {
          const lastMonth = subMonths(today, 1);
          return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
        },
      },
      {
        label: 'Last 90 days',
        getValue: () => [subDays(today, 89), today],
      },
      {
        label: 'Last quarter',
        getValue: () => {
          const lastQuarter = subQuarters(today, 1);
          return [startOfQuarter(lastQuarter), endOfQuarter(lastQuarter)];
        },
      },
      {
        label: 'Last year',
        getValue: () => [
          startOfYear(subYears(today, 1)),
          endOfYear(subYears(today, 1)),
        ],
      },
      {
        label: 'This year',
        getValue: () => [startOfYear(today), endOfYear(today)],
      },
    ],
    [today]
  );

  if (allTime && !shortcuts.some((shortcut) => shortcut.label === 'All time')) {
    shortcuts.push({
      label: 'All time',
      getValue: () => [subYears(today, 10), addYears(today, 10)],
    });
  }

  return (
    <Box
      sx={{
        p: mdDown ? 1 : 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <MuiStaticDateRangePicker
        {...props}
        value={dateRange}
        onChange={onDateRangeChange}
        slotProps={{
          shortcuts: {
            items: smDown ? [] : shortcuts,
          },
          actionBar: {
            actions: [],
          },
        }}
        calendars={mdDown ? 1 : 2}
        minDate={minDate}
        maxDate={maxDate}
      />
    </Box>
  );
};
